import type { TahseelFeeBreakdown } from '@kalba/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Building2,
  Calendar,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock,
  Download,
  ExternalLink,
  FileText,
  FlaskConical,
  Hash,
  Home,
  ListChecks,
  ListOrdered,
  Loader2,
  Mail,
  Phone,
  Send,
  Shield,
  User,
  UserCheck,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { AppLayout } from '../../../components/layout/app-layout';
import { AppModal } from '../../../components/ui/app-modal';
import { Button } from '../../../components/ui/button';
import { FieldBox } from '../../../components/ui/field-box';
import { StatusChevron } from '../../../components/ui/status-chevron';
import { Tabs } from '../../../components/ui/tabs';
import { usePermissions } from '../../../hooks/use-permissions';
import { api } from '../../../lib/ky';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';
import { ExtensionRequestsTab } from './ExtensionRequestsTab';
import { FeeBreakdownTab } from './FeeBreakdownTab';
import { LabRequestsTab } from './LabRequestsTab';
import { MunicipalityAttachmentsTab } from './MunicipalityAttachmentsTab';
import { SiteVisitsTab } from './SiteVisitsTab';
import { StudyReportTab } from './StudyReportTab';

// ── Types ──────────────────────────────────────────────────────────────────────

interface StudyRequestDetail {
  id: string;
  referenceNo: string;
  status: string;
  createdAt: string;
  reportSubmissionDate: string | null;
  reportValidityDate: string | null;
  crmCode: string | null;
  tahseelReferenceNo: string | null;
  sapOrderRef: string | null;
  requiredStudy: string | null;
  requiredStudyType: { id: string; name: string; code: string } | null;
  serviceCount: number | null;
  studyType: { id: string; name: string; code: string } | null;
  tahseelStudyRequestType: { id: string; name: string; code: string } | null;
  totalFee: string | null;
  feeBreakdown: TahseelFeeBreakdown | null;
  account: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    address: string | null;
  };
  contact: { id: string; fullName: string; email: string; phone: string | null };
  assignedConsultant: { id: string; name: string; email: string } | null;
  authorityAssignee: { id: string; name: string; email: string } | null;
  sapSyncStatus: 'PENDING' | 'SUCCESS' | 'FAILED' | null;
  siteVisits: {
    id: string;
    visitType: 'BEEAH_CONSULTANT' | 'LAB' | 'SM_CONSULTANT';
    status: 'NOT_STARTED' | 'CUSTOMER_APPROVAL' | 'APPROVED' | 'IN_PROGRESS' | 'COMPLETED';
  }[];
  labRequests: {
    id: string;
    status:
      | 'PENDING_LAB_ASSIGNMENT'
      | 'ASSIGNED_TO_LAB_TECHNICIAN'
      | 'IN_PROGRESS'
      | 'SUBMITTED_TO_CONSULTANT';
    assignedLabTechnicianId: string | null;
    assignedLabTechnician: { id: string; name: string; email: string } | null;
    siteVisitId: string | null;
  }[];
  studyReports: {
    id: string;
    status: 'DRAFT' | 'WAITING_FOR_APPROVAL' | 'APPROVED' | 'REJECTED';
    attachments: { id: string }[];
    approvals: {
      id: string;
      decision: 'DRAFT' | 'WAITING_FOR_APPROVAL' | 'APPROVED' | 'REJECTED';
      comments: string | null;
      reviewedAt: string;
      reviewedBy: { id: string; name: string };
    }[];
  }[];
  extensionRequests: { id: string; status: 'PENDING' | 'APPROVED' | 'REJECTED' }[];
}

interface AuditEntry {
  action: string;
  statusBefore: string | null;
  statusAfter: string | null;
  performedBy: { id: string; name: string };
  comments: string | null;
  timestamp: string;
}

interface UserListItem {
  id: string;
  name: string;
  email: string;
  roles: { role: { id: string; name: string } }[];
  userType: { id: string; name: string; code: string } | null;
}

// ── Status config ──────────────────────────────────────────────────────────────

const SHARJAH_STATUS_CHAIN = [
  { key: 'NEW', label: 'New' },
  { key: 'ASSIGNED', label: 'Assigned' },
  { key: 'SITE_VISIT_SCHEDULED', label: 'Site Visit' },
  { key: 'REQUEST_LAB_TEST', label: 'Lab Test' },
  { key: 'LAB_SITE_VISIT', label: 'Lab Site Visit' },
  { key: 'PREPARE_STUDY_REPORT', label: 'Preparing Study Report' },
  { key: 'STUDY_REPORT_SM_APPROVAL', label: 'Waiting for Approval' },
  { key: 'FINAL_REPORT_ISSUED', label: 'Report Issued' },
] as const;

type StatusKey = (typeof SHARJAH_STATUS_CHAIN)[number]['key'] | 'REJECTED';

/**
 * Chevron steps for the progress bar, built per-request rather than a fixed list —
 * "Lab Site Visit" and "SM Site Visit" are optional stages that only render once
 * that visit type has actually been scheduled, so a request that skips them doesn't
 * show a misleading step that was never reached.
 */
function buildStudyStatusSteps(request: StudyRequestDetail) {
  const hasLabSiteVisit = request.siteVisits.some((v) => v.visitType === 'LAB');
  const hasSmSiteVisit = request.siteVisits.some((v) => v.visitType === 'SM_CONSULTANT');

  const steps: { key: string; label: string }[] = [
    { key: 'NEW', label: 'New' },
    { key: 'ASSIGNED', label: 'Assigned' },
    { key: 'SITE_VISIT_SCHEDULED', label: 'Site Visit' },
    { key: 'REQUEST_LAB_TEST', label: 'Lab Test' },
  ];
  if (hasLabSiteVisit) steps.push({ key: 'LAB_SITE_VISIT', label: 'Lab Site Visit' });
  steps.push({ key: 'PREPARE_STUDY_REPORT', label: 'Preparing Study Report' });
  if (hasSmSiteVisit) steps.push({ key: 'SM_SITE_VISIT', label: 'SM Site Visit' });
  steps.push(
    { key: 'STUDY_REPORT_SM_APPROVAL', label: 'Waiting for Approval' },
    { key: 'FINAL_REPORT_ISSUED', label: 'Report Issued' },
  );
  return steps;
}

const STATUS_BADGE: Record<StatusKey, { label: string; className: string }> = {
  NEW: { label: 'New', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  ASSIGNED: { label: 'Assigned', className: 'bg-brand-50 text-brand-600 border-brand-200' },
  SITE_VISIT_SCHEDULED: {
    label: 'Site Visit',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
  },
  REQUEST_LAB_TEST: {
    label: 'Lab Test',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
  },
  LAB_SITE_VISIT: {
    label: 'Lab Site Visit',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
  },
  PREPARE_STUDY_REPORT: {
    label: 'Preparing Study Report',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
  },
  STUDY_REPORT_SM_APPROVAL: {
    label: 'Waiting for Approval',
    className: 'bg-brand-50 text-brand-600 border-brand-200',
  },
  FINAL_REPORT_ISSUED: {
    label: 'Report Issued',
    className: 'bg-success-50 text-success-600 border-success-200',
  },
  REJECTED: { label: 'Rejected', className: 'bg-error-50 text-error-600 border-error-200' },
};

// ── Route ──────────────────────────────────────────────────────────────────────

const searchSchema = z.object({
  tab: z
    .enum([
      'general',
      'site-visits',
      'lab-requests',
      'study-report',
      'extensions',
      'documents',
      'payment',
      'fee-breakdown',
      'audit',
    ])
    .optional(),
});

export const Route = createFileRoute('/study-requests/$requestId/')({
  validateSearch: searchSchema,
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('sm-study:view')) throw redirect({ to: '/dashboard' });
  },
  component: StudyRequestDetailPage,
});

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_BADGE[status as StatusKey] ?? {
    label: status,
    className: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold',
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  );
}

// ── Status Progress Indicator ──────────────────────────────────────────────────

function StatusProgress({ request }: { request: StudyRequestDetail }) {
  const status = request.status;
  if (status === 'REJECTED') {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-error-50 border border-error-200 px-4 py-3">
        <span className="h-2 w-2 rounded-full bg-error-500" />
        <span className="text-sm font-medium text-error-600">Rejected</span>
      </div>
    );
  }

  const hasSmSiteVisit = request.siteVisits.some((v) => v.visitType === 'SM_CONSULTANT');
  const hasCompletedSmSiteVisit = request.siteVisits.some(
    (v) => v.visitType === 'SM_CONSULTANT' && v.status === 'COMPLETED',
  );
  // While the request is in SM approval and a scheduled SM site visit hasn't been
  // completed yet, show "SM Site Visit" as the active step instead of jumping
  // straight to "Waiting for Approval" — both share the same backend status.
  const currentKey =
    status === 'STUDY_REPORT_SM_APPROVAL' && hasSmSiteVisit && !hasCompletedSmSiteVisit
      ? 'SM_SITE_VISIT'
      : status;

  return <StatusChevron steps={buildStudyStatusSteps(request)} currentKey={currentKey} />;
}

// ── Assign modal ───────────────────────────────────────────────────────────────

function AssignModal({
  open,
  onClose,
  title,
  description,
  roleName,
  userTypeCodes,
  onAssign,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  roleName: string;
  /** When provided, filter by User.userType.code instead of role name (e.g. ['BEEAH_CONSULTANT', 'SM_CONSULTANT']). */
  userTypeCodes?: string[];
  onAssign: (userId: string) => void;
  isPending: boolean;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users', 'consultants'],
    queryFn: () => api.get('users/consultants').json<UserListItem[]>(),
    enabled: open,
    staleTime: 60_000,
  });

  const filtered = users.filter(
    (u) =>
      (userTypeCodes
        ? !!u.userType && userTypeCodes.includes(u.userType.code)
        : u.roles.some((r) => r.role.name === roleName)) &&
      (u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <AppModal open={open} onClose={onClose} title={title} description={description}>
      <div className="space-y-4">
        <input
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
        <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 size={16} className="animate-spin text-gray-400" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-6 text-center text-xs text-gray-400">No users found</p>
          ) : (
            filtered.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => setSelectedId(u.id)}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-gray-50',
                  selectedId === u.id && 'bg-brand-50',
                )}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
                  <User size={13} className="text-gray-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800">{u.name}</p>
                  <p className="truncate text-xs text-gray-400">{u.email}</p>
                </div>
                {u.userType && (
                  <span className="ml-auto shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                    {u.userType.name}
                  </span>
                )}
                {selectedId === u.id && (
                  <span className="ml-2 shrink-0 text-xs font-semibold text-brand-600">
                    Selected
                  </span>
                )}
              </button>
            ))
          )}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            disabled={!selectedId || isPending}
            onClick={() => onAssign(selectedId)}
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Assigning…
              </span>
            ) : (
              'Assign'
            )}
          </Button>
        </div>
      </div>
    </AppModal>
  );
}

// ── General Info Tab ───────────────────────────────────────────────────────────

function GeneralInfoTab({ request }: { request: StudyRequestDetail }) {
  const latestStudyReport = request.studyReports[0] ?? null;
  const rejection =
    latestStudyReport?.status === 'REJECTED'
      ? latestStudyReport.approvals.find((a) => a.decision === 'REJECTED')
      : undefined;

  return (
    <div className="space-y-6">
      {rejection && (
        <div className="rounded-2xl border border-error-200 bg-error-50 px-5 py-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-error-500">
            Study Report Rejected
          </p>
          <p className="text-sm leading-relaxed text-error-700">{rejection.comments ?? '—'}</p>
          <p className="mt-1.5 text-xs text-error-400">
            by {rejection.reviewedBy.name} · {fmt(rejection.reviewedAt)}
          </p>
        </div>
      )}
      {/* Study Details */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50/50 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-700">Study Details</h3>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 px-5 py-5">
          <FieldBox icon={<Hash size={16} />} label="Reference No">
            <span className="font-mono font-bold text-brand-600">{request.referenceNo}</span>
          </FieldBox>
          {request.tahseelStudyRequestType && (
            <FieldBox icon={<ClipboardList size={16} />} label="Study Type">
              {request.tahseelStudyRequestType.name}
            </FieldBox>
          )}
          <FieldBox icon={<ListChecks size={16} />} label="Required Study">
            {request.requiredStudyType?.name ?? request.requiredStudy ?? '—'}
          </FieldBox>
          <FieldBox icon={<ListOrdered size={16} />} label="Service Count">
            {request.serviceCount != null ? String(request.serviceCount) : '—'}
          </FieldBox>
          <FieldBox icon={<FileText size={16} />} label="CRM Code">
            {request.crmCode ?? '—'}
          </FieldBox>
          <FieldBox icon={<ExternalLink size={16} />} label="Tahseel Reference Number">
            {request.tahseelReferenceNo ?? '—'}
          </FieldBox>
          <FieldBox icon={<ExternalLink size={16} />} label="SAP Sales Order Number">
            {request.sapOrderRef ?? '—'}
          </FieldBox>
          <FieldBox icon={<Calendar size={16} />} label="Report Submission Date">
            {request.reportSubmissionDate ? fmt(request.reportSubmissionDate) : '—'}
          </FieldBox>
          <FieldBox icon={<CalendarClock size={16} />} label="Report Validity Date">
            {request.reportValidityDate ? fmt(request.reportValidityDate) : '—'}
          </FieldBox>
          <FieldBox icon={<Clock size={16} />} label="Created At">
            {fmt(request.createdAt)}
          </FieldBox>
        </div>
      </div>

      {/* Account & Assignment */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-gray-100 bg-gray-50/50 px-5 py-3 flex items-center gap-2">
            <Building2 size={14} className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700">Account</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 px-5 py-5">
            <FieldBox icon={<Building2 size={16} />} label="Name">
              <span className="font-medium text-gray-900">{request.account.name}</span>
            </FieldBox>
            {request.account.email && (
              <FieldBox icon={<Mail size={16} />} label="Email">
                {request.account.email}
              </FieldBox>
            )}
            {request.account.phone && (
              <FieldBox icon={<Phone size={16} />} label="Phone">
                {request.account.phone}
              </FieldBox>
            )}
            <FieldBox icon={<User size={16} />} label="Contact Name">
              <span className="font-medium text-gray-900">{request.contact.fullName}</span>
            </FieldBox>
            <FieldBox icon={<Mail size={16} />} label="Contact Email">
              {request.contact.email}
            </FieldBox>
            {request.contact.phone && (
              <FieldBox icon={<Phone size={16} />} label="Contact Phone">
                {request.contact.phone}
              </FieldBox>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-gray-100 bg-gray-50/50 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-700">Assignment</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 px-5 py-5">
            <FieldBox icon={<UserCheck size={16} />} label="Beeah Consultant">
              {request.assignedConsultant ? (
                <span className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-100">
                    <User size={11} className="text-brand-600" />
                  </div>
                  <span>
                    {request.assignedConsultant.name}
                    <span className="ml-1.5 text-xs text-gray-400">
                      {request.assignedConsultant.email}
                    </span>
                  </span>
                </span>
              ) : (
                <span className="italic text-gray-400">Unassigned</span>
              )}
            </FieldBox>
            <FieldBox icon={<Shield size={16} />} label="SM Authority Assignee">
              {request.authorityAssignee ? (
                <span className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100">
                    <User size={11} className="text-purple-600" />
                  </div>
                  <span>
                    {request.authorityAssignee.name}
                    <span className="ml-1.5 text-xs text-gray-400">
                      {request.authorityAssignee.email}
                    </span>
                  </span>
                </span>
              ) : (
                <span className="italic text-gray-400">Unassigned</span>
              )}
            </FieldBox>
            {(() => {
              const assignedLab = request.labRequests.find((lr) => lr.assignedLabTechnician);
              return (
                <FieldBox icon={<FlaskConical size={16} />} label="Lab Technician">
                  {assignedLab?.assignedLabTechnician ? (
                    <span className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100">
                        <User size={11} className="text-emerald-600" />
                      </div>
                      <span>
                        {assignedLab.assignedLabTechnician.name}
                        <span className="ml-1.5 text-xs text-gray-400">
                          {assignedLab.assignedLabTechnician.email}
                        </span>
                      </span>
                    </span>
                  ) : (
                    <span className="italic text-gray-400">Unassigned</span>
                  )}
                </FieldBox>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Audit Log Tab ──────────────────────────────────────────────────────────────

const AUDIT_ACTION_LABELS: Record<string, string> = {
  CREATED: 'Study Request Created',
  UPDATED: 'Updated',
  CONSULTANT_ASSIGNED: 'Consultant Assigned',
  AUTHORITY_ASSIGNED: 'Authority Assignee Set',
  SITE_VISIT_SCHEDULED: 'Site Visit Scheduled',
  LAB_TEST_REQUESTED: 'Lab Test Requested',
  STUDY_REPORT_SUBMITTED: 'Study Report Submitted',
  PAYMENT_PROCESS: 'Payment Initiated',
  PAID: 'Payment Received',
  FINAL_REPORT_ISSUED: 'Final Report Issued',
  SENT_TO_SAP: 'Sent to SAP',
  SAP_SYNC_FAILED: 'SAP Sync Failed',
  REJECTED: 'Rejected',
};

function AuditLogTab({ requestId }: { requestId: string }) {
  const {
    data: logs,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['study-audit', requestId],
    queryFn: () => api.get(`study-requests/${requestId}/audit`).json<AuditEntry[]>(),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-12 rounded-xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-12 text-center text-sm text-error-500">
        Could not load audit log.
      </div>
    );
  }

  if (!logs?.length) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-12 text-center text-sm text-gray-400">
        No audit entries yet.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="border-b border-gray-100 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-gray-700">Activity Log</h3>
        <p className="text-xs text-gray-400 mt-0.5">{logs.length} entries</p>
      </div>
      <div className="divide-y divide-gray-100">
        {logs.map((entry, i) => (
          <div
            key={i}
            className="flex items-start gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors"
          >
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500">
              <span className="text-[10px] font-bold">●</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-gray-800">
                  {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
                </span>
                {entry.statusBefore && entry.statusAfter && (
                  <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md">
                    {entry.statusBefore} → {entry.statusAfter}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-gray-400">
                by {entry.performedBy.name ?? entry.performedBy.id}
              </p>
              {entry.comments && (
                <p className="mt-1.5 rounded-lg bg-gray-50 border border-gray-100 px-3 py-1.5 text-xs text-gray-700">
                  {entry.comments}
                </p>
              )}
            </div>
            <span className="shrink-0 text-xs text-gray-400 whitespace-nowrap">
              {new Date(entry.timestamp).toLocaleString('en-AE', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

function StudyRequestDetailPage() {
  const { requestId } = Route.useParams();
  const { tab: tabFromUrl } = Route.useSearch();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { user } = useAuthStore();
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState<
    | 'general'
    | 'site-visits'
    | 'lab-requests'
    | 'study-report'
    | 'extensions'
    | 'documents'
    | 'fee-breakdown'
    | 'audit'
  >(tabFromUrl === 'payment' ? 'general' : (tabFromUrl ?? 'general'));
  const [assignConsultantOpen, setAssignConsultantOpen] = useState(false);
  const [scheduleVisitTrigger, setScheduleVisitTrigger] = useState(0);
  const [labRequestTrigger, setLabRequestTrigger] = useState(0);
  const [studyReportTrigger, setStudyReportTrigger] = useState(0);
  const [scheduleLabVisitTrigger, setScheduleLabVisitTrigger] = useState(0);
  const [approveReportTrigger, setApproveReportTrigger] = useState(0);
  const [rejectReportTrigger, setRejectReportTrigger] = useState(0);
  const [scheduleSmVisitTrigger, setScheduleSmVisitTrigger] = useState(0);
  const [extensionTrigger, setExtensionTrigger] = useState(0);

  const requestKey = ['study-request', requestId];

  const {
    data: request,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: requestKey,
    queryFn: () => api.get(`study-requests/${requestId}`).json<StudyRequestDetail>(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: requestKey });

  const assignConsultantMutation = useMutation({
    mutationFn: (consultantId: string) =>
      api.patch(`study-requests/${requestId}/assign-consultant`, { json: { consultantId } }).json(),
    onSuccess: () => {
      setAssignConsultantOpen(false);
      invalidate();
    },
  });

  const sendToSapMutation = useMutation({
    mutationFn: () => api.patch(`study-requests/${requestId}/send-to-sap`).json(),
    onSuccess: invalidate,
  });

  const assignLabRequestMutation = useMutation({
    mutationFn: () =>
      api
        .patch(`study-requests/${requestId}/lab-requests/${pendingLabRequest?.id}/assign-to-me`)
        .json(),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['lab-requests', requestId] });
    },
  });

  const submitStudyReportMutation = useMutation({
    mutationFn: () =>
      api.post(`study-requests/${requestId}/study-reports/${latestStudyReport?.id}/submit`).json(),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['study-reports', requestId] });
    },
  });

  const downloadContractMutation = useMutation({
    mutationFn: () =>
      api
        .get(
          `study-requests/${requestId}/study-reports/${latestStudyReport?.id}/environmental-study-contract`,
        )
        .json<{ downloadUrl: string; expiresAt: string }>(),
    onSuccess: (data) => window.open(data.downloadUrl, '_blank'),
    onError: async (err: unknown) => {
      let description = 'Please try again, or contact support if this keeps happening.';
      if (err && typeof err === 'object' && 'response' in err) {
        try {
          const body = await (err as { response: Response }).response.json();
          if (body?.message) {
            description = Array.isArray(body.message) ? body.message.join(' · ') : body.message;
          }
        } catch {
          // response wasn't JSON — fall back to the generic message above
        }
      }
      toast.error('Could not generate the study contract', { description, duration: 6000 });
    },
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <div className="h-8 w-64 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-96 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-80 animate-pulse rounded bg-gray-100" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !request) {
    return (
      <AppLayout>
        <div className="py-20 text-center">
          <p className="mb-3 text-sm text-error-500">Failed to load study request</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-sm font-medium text-brand-600 underline hover:text-brand-700"
          >
            Retry
          </button>
        </div>
      </AppLayout>
    );
  }

  const sentToSap = request.sapSyncStatus === 'SUCCESS';
  const hasConsultant = !!request.assignedConsultant;
  const hasBeeahSiteVisit = request.siteVisits.some((v) => v.visitType === 'BEEAH_CONSULTANT');
  const canSendToSap = can('sm-study:send-to-sap') && request.status === 'NEW' && !sentToSap;
  const canAssignConsultant =
    can('sm-study:assign-consultant') && (request.status !== 'NEW' || sentToSap) && !hasConsultant;
  const canScheduleSiteVisit = can('sm-site-visit:create') && hasConsultant && !hasBeeahSiteVisit;
  const hasCompletedBeeahVisit = request.siteVisits.some(
    (v) => v.visitType === 'BEEAH_CONSULTANT' && v.status === 'COMPLETED',
  );
  const allLabRequestsSubmitted = request.labRequests.every(
    (lr) => lr.status === 'SUBMITTED_TO_CONSULTANT',
  );
  const latestStudyReport = request.studyReports[0] ?? null;
  const isLatestStudyReportEditable =
    !!latestStudyReport &&
    (latestStudyReport.status === 'DRAFT' || latestStudyReport.status === 'REJECTED');
  const canSubmitStudyReport = can('sm-study-report:create') && isLatestStudyReportEditable;
  const hasStudyReportAttachment = (latestStudyReport?.attachments.length ?? 0) > 0;
  const isReportWaitingForApproval = latestStudyReport?.status === 'WAITING_FOR_APPROVAL';
  const hasSmSiteVisit = request.siteVisits.some((v) => v.visitType === 'SM_CONSULTANT');
  // SM Consultant only: has study-report:approve/reject but NOT lab:create
  const canApproveReport =
    can('sm-study-report:approve') && !can('sm-lab:create') && isReportWaitingForApproval;
  const canRejectReport =
    can('sm-study-report:reject') && !can('sm-lab:create') && isReportWaitingForApproval;
  const canScheduleSmVisit =
    can('sm-site-visit:create') && !can('sm-lab:create') && !hasSmSiteVisit;
  const hasLabRequest = request.labRequests.length > 0;
  // Once the study report is rejected, the Beeah Consultant may request another lab test.
  const canRequestLabStudy =
    can('sm-lab:create') &&
    hasCompletedBeeahVisit &&
    (!hasLabRequest || latestStudyReport?.status === 'REJECTED');
  const pendingLabRequest = request.labRequests.find(
    (lr) => lr.status === 'PENDING_LAB_ASSIGNMENT',
  );
  const canAssignLabRequest = can('sm-lab:assign') && !!pendingLabRequest;
  const myLabRequestNeedingVisit = request.labRequests.find(
    (lr) =>
      !!user &&
      lr.assignedLabTechnicianId === user.id &&
      !lr.siteVisitId &&
      lr.status !== 'SUBMITTED_TO_CONSULTANT',
  );
  const canScheduleLabVisit = can('sm-site-visit:create') && !!myLabRequestNeedingVisit;
  const hasStudyReport = request.studyReports.length > 0;
  const canPrepareStudyReport =
    can('sm-study-report:create') &&
    hasCompletedBeeahVisit &&
    allLabRequestsSubmitted &&
    !hasStudyReport;
  // Visible until the study report is submitted for SM approval — i.e. no report yet, or the
  // latest one is still DRAFT/REJECTED (reuses isLatestStudyReportEditable for that DRAFT/REJECTED check) —
  // and only while no extension request is already awaiting an SM decision.
  const hasPendingExtensionRequest = request.extensionRequests.some((e) => e.status === 'PENDING');
  const canRequestExtension =
    can('sm-extension:create') &&
    (!latestStudyReport || isLatestStudyReportEditable) &&
    !hasPendingExtensionRequest;
  const canDownloadContract =
    can('sm-study-report:view') && latestStudyReport?.status === 'APPROVED';
  const canViewFees = can('sm-study:view:feeBreakdown');

  const showFeeBreakdownTab = !!request.tahseelReferenceNo && canViewFees;

  const tabs = [
    { id: 'general', label: 'General Info' },
    { id: 'site-visits', label: 'Site Visits' },
    { id: 'lab-requests', label: 'Lab Requests' },
    { id: 'study-report', label: 'Study Report' },
    { id: 'extensions', label: 'Extensions' },
    { id: 'documents', label: 'Documents' },
    ...(showFeeBreakdownTab ? [{ id: 'fee-breakdown', label: 'Fee Breakdown' }] : []),
    { id: 'audit', label: 'Audit Log' },
  ] as const;

  return (
    <AppLayout>
      {/* Breadcrumb */}
      <nav className="mb-5 flex items-center gap-1.5 text-xs text-gray-400">
        <button
          type="button"
          onClick={() => navigate({ to: '/dashboard' })}
          className="shrink-0 transition-colors hover:text-gray-600"
        >
          <Home size={12} />
        </button>
        <ChevronRight size={12} className="shrink-0" />
        <button
          type="button"
          onClick={() => navigate({ to: '/study-requests', search: { page: 1, limit: 20 } })}
          className="font-medium transition-colors hover:text-gray-600"
        >
          Study Requests
        </button>
        <ChevronRight size={12} className="shrink-0" />
        <span className="font-mono font-medium text-gray-600">{request.referenceNo}</span>
      </nav>

      {/* Header card */}
      <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-white via-white to-gray-50/50 shadow-theme-sm">
        <div className="p-6">
          {/* Title row */}
          <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50">
                  <ClipboardList size={20} className="text-brand-500" />
                </div>
                <h1 className="font-mono text-2xl font-bold tracking-tight text-gray-900">
                  {request.referenceNo}
                </h1>
                <StatusBadge status={request.status} />
              </div>
              {request.studyType && (
                <span className="inline-flex items-center gap-1.5 rounded-xl border border-brand-200 bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-700">
                  <FileText size={13} />
                  {request.studyType.name}
                </span>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2 md:shrink-0">
              {canSendToSap && (
                <Button
                  variant="outline"
                  onClick={() => sendToSapMutation.mutate()}
                  disabled={sendToSapMutation.isPending}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <ExternalLink size={14} />
                  {sendToSapMutation.isPending ? 'Sending…' : 'Send to SAP'}
                </Button>
              )}
              {canAssignConsultant && (
                <Button
                  variant="outline"
                  onClick={() => setAssignConsultantOpen(true)}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <User size={14} />
                  Assign Consultant
                </Button>
              )}
              {canScheduleSiteVisit && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setActiveTab('site-visits');
                    setScheduleVisitTrigger((n) => n + 1);
                  }}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <Calendar size={14} />
                  Schedule Site Visit
                </Button>
              )}
              {canRequestLabStudy && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setActiveTab('lab-requests');
                    setLabRequestTrigger((n) => n + 1);
                  }}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <FlaskConical size={14} />
                  Request Lab Test
                </Button>
              )}
              {canAssignLabRequest && (
                <Button
                  variant="outline"
                  onClick={() => assignLabRequestMutation.mutate()}
                  disabled={assignLabRequestMutation.isPending}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <UserCheck size={14} />
                  {assignLabRequestMutation.isPending ? 'Assigning…' : 'Assign Lab Request to Me'}
                </Button>
              )}
              {canScheduleLabVisit && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setActiveTab('lab-requests');
                    setScheduleLabVisitTrigger((n) => n + 1);
                  }}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <Calendar size={14} />
                  Schedule Lab Site Visit
                </Button>
              )}
              {canPrepareStudyReport && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setActiveTab('study-report');
                    setStudyReportTrigger((n) => n + 1);
                  }}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <FileText size={14} />
                  Prepare Study Report
                </Button>
              )}
              {canRequestExtension && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setActiveTab('extensions');
                    setExtensionTrigger((n) => n + 1);
                  }}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <Clock size={14} />
                  Request Extension
                </Button>
              )}
              {canSubmitStudyReport && (
                <Button
                  variant="outline"
                  onClick={() => submitStudyReportMutation.mutate()}
                  disabled={
                    !hasStudyReportAttachment ||
                    !allLabRequestsSubmitted ||
                    submitStudyReportMutation.isPending
                  }
                  title={
                    !hasStudyReportAttachment
                      ? 'Attach the study report document'
                      : !allLabRequestsSubmitted
                        ? 'Complete pending lab test(s) before submitting'
                        : undefined
                  }
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <Send size={14} />
                  {submitStudyReportMutation.isPending ? 'Submitting…' : 'Submit for SM Approval'}
                </Button>
              )}
              {canDownloadContract && (
                <Button
                  variant="outline"
                  onClick={() => downloadContractMutation.mutate()}
                  disabled={downloadContractMutation.isPending}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <Download size={14} />
                  {downloadContractMutation.isPending ? 'Preparing…' : 'Download Study Contract'}
                </Button>
              )}
              {canApproveReport && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setActiveTab('study-report');
                    setApproveReportTrigger((n) => n + 1);
                  }}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <CheckCircle2 size={14} />
                  Approve
                </Button>
              )}
              {canRejectReport && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setActiveTab('study-report');
                    setRejectReportTrigger((n) => n + 1);
                  }}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <X size={14} />
                  Reject
                </Button>
              )}
              {canScheduleSmVisit && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setActiveTab('study-report');
                    setScheduleSmVisitTrigger((n) => n + 1);
                  }}
                  className="flex items-center gap-2 rounded-xl border-gray-200 hover:bg-gray-100"
                >
                  <Calendar size={14} />
                  Schedule SM Site Visit
                </Button>
              )}
            </div>
          </div>

          {/* Status Progress */}
          <div className="mt-4 overflow-x-auto pb-2">
            <div className="min-w-[600px]">
              <StatusProgress request={request} />
            </div>
          </div>

          {/* Meta row */}
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-100 pt-4 text-xs text-gray-400">
            <span className="flex items-center gap-1.5">
              <Building2 size={12} />
              {request.account.name}
            </span>
            <span className="flex items-center gap-1.5">
              <User size={12} />
              {request.contact.fullName}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={12} />
              Created {fmt(request.createdAt)}
            </span>
            {request.reportSubmissionDate && (
              <span className="flex items-center gap-1.5">
                <FlaskConical size={12} />
                Due {fmt(request.reportSubmissionDate)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={tabs as unknown as { id: string; label: string }[]}
        activeTab={activeTab}
        onTabChange={(id) =>
          setActiveTab(
            id as
              | 'general'
              | 'site-visits'
              | 'lab-requests'
              | 'study-report'
              | 'extensions'
              | 'documents'
              | 'fee-breakdown'
              | 'audit',
          )
        }
        className="mb-5"
      />

      {activeTab === 'general' && <GeneralInfoTab request={request} />}
      {activeTab === 'site-visits' && (
        <SiteVisitsTab
          requestId={requestId}
          defaultAddress={request.account.address ?? undefined}
          autoOpenTrigger={scheduleVisitTrigger}
          onMutated={invalidate}
        />
      )}
      {activeTab === 'lab-requests' && (
        <LabRequestsTab
          requestId={requestId}
          defaultAddress={request.account.address ?? undefined}
          autoOpenTrigger={labRequestTrigger}
          scheduleVisitTrigger={scheduleLabVisitTrigger}
          onMutated={invalidate}
        />
      )}
      {activeTab === 'study-report' && (
        <StudyReportTab
          requestId={requestId}
          defaultAddress={request.account.address ?? undefined}
          onMutated={invalidate}
          autoOpenTrigger={studyReportTrigger}
          approveTrigger={approveReportTrigger}
          rejectTrigger={rejectReportTrigger}
          scheduleSmVisitTrigger={scheduleSmVisitTrigger}
        />
      )}
      {activeTab === 'extensions' && (
        <ExtensionRequestsTab
          requestId={requestId}
          reportSubmissionDate={request.reportSubmissionDate}
          onMutated={invalidate}
          autoOpenTrigger={extensionTrigger}
        />
      )}
      {activeTab === 'documents' && <MunicipalityAttachmentsTab requestId={requestId} />}
      {activeTab === 'fee-breakdown' && showFeeBreakdownTab && (
        <FeeBreakdownTab feeBreakdown={request.feeBreakdown} totalFee={request.totalFee} />
      )}
      {activeTab === 'audit' && <AuditLogTab requestId={requestId} />}

      {/* Assign Consultant Modal */}
      <AssignModal
        open={assignConsultantOpen}
        onClose={() => setAssignConsultantOpen(false)}
        title="Assign Consultant"
        description="Select a Beeah Consultant or SM Consultant to assign to this study request."
        roleName="BEEAH_CONSULTANT"
        userTypeCodes={['BEEAH_CONSULTANT', 'SM_CONSULTANT']}
        onAssign={(userId) => assignConsultantMutation.mutate(userId)}
        isPending={assignConsultantMutation.isPending}
      />
    </AppLayout>
  );
}
